import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button } from '@mui/material';

import {
    loadInterventionTypes,
    loadSmokingCoResults,
    loadSmokingSelfReportedQuits
} from '../../../../store/directusService';
import {
    deleteSmokingQuitAttemptReview,
    onDeleteSmokingQuitAttemptReview
} from '../../../../store/participantService';
import { SCREEN_SETTINGS } from '../../../../themes/theme';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreateQuitReview from './CreateQuitReview';
import QuitReviewTable from './QuitReviewTable';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const QuitReviewManagement = ({
    participant,
    quitDate,
    smokingQuitAttemptId,
    isDisabled,
    isDeleteDisabled
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const deleteMsg = `Smoking quit attempt review deleted`;

    // STORE STATE
    const quitReviews = useSelector(
        (state) => state.entities.participantService.smokingQuitAttemptReviews[smokingQuitAttemptId]
    );
    const interventionTypes = useSelector(
        (state) => state.entities.directusService.interventionTypes
    );
    const selfReportedQuits = useSelector(
        (state) => state.entities.directusService.smokingSelfReportedQuits
    );
    const coResults = useSelector((state) => state.entities.directusService.smokingCoResults);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS

    useEffect(() => {
        interventionTypes?.length < 1 && dispatch(loadInterventionTypes());
        selfReportedQuits?.length < 1 && dispatch(loadSmokingSelfReportedQuits());
        coResults?.length < 1 && dispatch(loadSmokingCoResults());
    }, []);

    useEffect(() => {
        if (!quitReviews) return;
        const rows = quitReviews
            .filter((el) => !el.inactive)
            .map((el) => ({
                ...el,
                intervention:
                    interventionTypes.find((entry) => entry.id === el.interventionTypeId)?.name ||
                    '',
                selfReportedQuit:
                    selfReportedQuits.find((entry) => entry.id === el.selfReportedQuitId)?.name ||
                    '',
                coResult: coResults.find((entry) => entry.id === el.coResultId)?.name || ''
            }));
        setRows(rows);
    }, [quitReviews, interventionTypes, selfReportedQuits, coResults]);

    useEffect(() => {
        if (successMessage === deleteMsg)
            dispatch(onDeleteSmokingQuitAttemptReview(smokingQuitAttemptId, selectedRow.id));
    }, [successMessage]);

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setShowForm(true);
    };

    const onEdit = (row) => {
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteSmokingQuitAttemptReview(row.id, deleteMsg));
    };

    return (
        <div>
            <div className={app.sectionHeading}>Quit Reviews</div>
            <div>
                {rows.length < 1 ? (
                    <LoadingSpinner content="No quit reviews found" />
                ) : (
                    <QuitReviewTable
                        rows={rows}
                        isDisabled={isDisabled || showForm}
                        isDeleteDisabled={isDeleteDisabled || showForm}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                )}
            </div>
            <div className={local.actions}>
                {!showForm && (
                    <Button
                        disabled={isDisabled}
                        color="primary"
                        variant="contained"
                        size={'small'}
                        onClick={onCreate}>
                        Add New Quit Review
                        <Box width={SCREEN_SETTINGS.gap.medium} />
                        <AddCircleOutlineIcon />
                    </Button>
                )}
                <div>
                    {showForm && (
                        <CreateQuitReview
                            participant={participant}
                            onClose={() => setShowForm(false)}
                            isDisabled={isDisabled}
                            smokingQuitAttemptId={smokingQuitAttemptId}
                            quitDate={quitDate}
                            type={formType}
                            quitAttemptReview={selectedRow}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

QuitReviewManagement.propTypes = {
    participant: PropTypes.object.isRequired,
    quitDate: PropTypes.string,
    smokingQuitAttemptId: PropTypes.string,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool
};

export default QuitReviewManagement;
